import { useState, useRef, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Mail, Lock, Eye, EyeOff, Building2, Phone, Upload, Image, X, ArrowRight, Check, FileText, Ticket, Gift } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RegisterPage = () => {
  const [searchParams] = useSearchParams();
  
  useEffect(() => {
    window.scrollTo(0, 0);
    // Check if there's a promo code in URL
    const urlPromoCode = searchParams.get('promo') || searchParams.get('code');
    if (urlPromoCode) {
      setPromoCode(urlPromoCode);
      validatePromoCode(urlPromoCode);
    }
  }, []);

  const [formData, setFormData] = useState({
    email: '',
    password: '',
    business_name: '',
    phone: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [acceptedTerms, setAcceptedTerms] = useState(false);
  const [termsModalOpen, setTermsModalOpen] = useState(false);
  const [promoCode, setPromoCode] = useState('');
  const [promoValid, setPromoValid] = useState(null);
  const [promoInfo, setPromoInfo] = useState(null);
  const [validatingPromo, setValidatingPromo] = useState(false);
  const fileInputRef = useRef(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const validatePromoCode = async (code) => {
    if (!code || code.length < 6) {
      setPromoValid(null);
      setPromoInfo(null);
      return;
    }
    
    setValidatingPromo(true);
    try {
      const response = await axios.post(`${API}/promo-codes/validate?code=${code}`);
      setPromoValid(true);
      setPromoInfo(response.data);
    } catch (error) {
      setPromoValid(false);
      setPromoInfo({ message: error.response?.data?.detail || 'Código no válido' });
    } finally {
      setValidatingPromo(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handlePromoCodeChange = (e) => {
    const code = e.target.value.toUpperCase();
    setPromoCode(code);
    if (code.length >= 6) {
      validatePromoCode(code);
    } else {
      setPromoValid(null);
      setPromoInfo(null);
    }
  };

  const handleLogoSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.startsWith('image/')) {
        toast.error('Por favor selecciona una imagen');
        return;
      }
      if (file.size > 5 * 1024 * 1024) {
        toast.error('La imagen no puede superar los 5MB');
        return;
      }
      setLogoFile(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setLogoPreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeLogo = () => {
    setLogoFile(null);
    setLogoPreview(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!acceptedTerms) {
      toast.error('Debes aceptar los términos y condiciones');
      return;
    }
    
    setLoading(true);
    
    try {
      // Include promo code if valid
      const registerData = { ...formData };
      if (promoCode && promoValid) {
        registerData.promo_code = promoCode;
      }
      
      await register(registerData);
      
      if (logoFile) {
        const logoFormData = new FormData();
        logoFormData.append('file', logoFile);
        
        try {
          await axios.post(`${API}/upload/logo`, logoFormData, {
            headers: { 'Content-Type': 'multipart/form-data' }
          });
        } catch (logoError) {
          console.error('Logo upload error:', logoError);
          toast.warning('Cuenta creada, pero hubo un error al subir el logo.');
        }
      }
      
      toast.success('¡Cuenta creada! Bienvenido a Stampply');
      navigate('/dashboard');
    } catch (error) {
      console.error('Register error:', error);
      toast.error(error.response?.data?.detail || 'Error al crear la cuenta');
    } finally {
      setLoading(false);
    }
  };

  const benefits = [
    "Configura tu programa en 5 minutos",
    "Sin tarjeta de crédito",
    "Soporte incluido",
    "Cancela cuando quieras"
  ];

  return (
    <div className="min-h-screen flex">
      {/* Left - Branding */}
      <div className="hidden lg:flex flex-1 bg-primary relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmZmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PGNpcmNsZSBjeD0iMzAiIGN5PSIzMCIgcj0iMiIvPjwvZz48L2c+PC9zdmc+')]" />
        
        <div className="relative z-10 flex flex-col items-start justify-center w-full p-12 lg:p-16">
          <Link to="/" className="mb-12">
            <img 
              src="https://customer-assets.emergentagent.com/job_796fbc31-c145-4a59-88df-6b2e6dbd2615/artifacts/eca23z1z_LOGO%20STAMPPLY%202.png" 
              alt="Stampply Rewards" 
              className="h-36 sm:h-40 lg:h-44 w-auto"
            />
          </Link>
          
          <div className="max-w-md">
            <h2 className="text-4xl font-bold text-white mb-6">
              Empieza a fidelizar clientes hoy
            </h2>
            <p className="text-white/80 text-lg mb-8">
              Únete a cientos de comercios que ya usan Stampply para aumentar sus visitas recurrentes.
            </p>
            
            <ul className="space-y-4">
              {benefits.map((benefit, i) => (
                <li key={i} className="flex items-center gap-3 text-white/90">
                  <div className="w-6 h-6 rounded-full bg-secondary/20 flex items-center justify-center">
                    <Check className="w-4 h-4 text-secondary" />
                  </div>
                  {benefit}
                </li>
              ))}
            </ul>
          </div>
          
          <div className="mt-auto pt-12">
            <img
              src="https://customer-assets.emergentagent.com/job_stampply-rewards/artifacts/wfagus5d_stampply%20altas.png"
              alt="Stampply Dashboard Preview"
              className="w-full max-w-md drop-shadow-2xl"
            />
          </div>
        </div>
        
        <div className="absolute top-20 -right-20 w-80 h-80 bg-secondary/20 rounded-full blur-3xl" />
        <div className="absolute -bottom-20 -left-20 w-96 h-96 bg-blue-500/20 rounded-full blur-3xl" />
      </div>

      {/* Right - Form */}
      <div className="flex-1 flex items-center justify-center p-6 sm:p-12 bg-white overflow-y-auto">
        <motion.div 
          className="w-full max-w-md"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Link to="/" className="lg:hidden inline-block mb-8">
            <img 
              src="https://customer-assets.emergentagent.com/job_796fbc31-c145-4a59-88df-6b2e6dbd2615/artifacts/n7zu75fp_file_00000000fb6c7243ac605c38dbc5a990%20%281%29.png" 
              alt="Stampply Rewards" 
              className="h-16 sm:h-20 w-auto"
            />
          </Link>

          <h1 className="text-3xl sm:text-4xl font-bold text-slate-900 mb-2">
            Crea tu cuenta gratis
          </h1>
          <p className="text-slate-600 mb-8">
            Empieza a fidelizar clientes en minutos
          </p>

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="space-y-2">
              <Label htmlFor="business_name" className="text-slate-700 font-medium">
                Nombre del negocio
              </Label>
              <div className="relative">
                <Building2 className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="business_name"
                  name="business_name"
                  type="text"
                  placeholder="Mi comercio"
                  value={formData.business_name}
                  onChange={handleChange}
                  className="pl-12 h-14 text-base rounded-xl border-slate-300 focus:border-primary focus:ring-primary/20"
                  required
                  data-testid="register-business-input"
                />
              </div>
            </div>

            {/* Logo Upload */}
            <div className="space-y-2">
              <Label className="text-slate-700 font-medium">Logo del negocio</Label>
              <div className="flex items-center gap-4">
                {logoPreview ? (
                  <div className="relative">
                    <img 
                      src={logoPreview} 
                      alt="Logo preview" 
                      className="w-20 h-20 object-contain rounded-xl border border-slate-200 bg-white p-1"
                    />
                    <button
                      type="button"
                      onClick={removeLogo}
                      className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <div 
                    onClick={() => fileInputRef.current?.click()}
                    className="w-20 h-20 border-2 border-dashed border-slate-300 rounded-xl flex flex-col items-center justify-center cursor-pointer hover:border-primary hover:bg-primary/5 transition-all"
                  >
                    <Image className="w-6 h-6 text-slate-400" />
                  </div>
                )}
                <div className="flex-1">
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => fileInputRef.current?.click()}
                    className="w-full h-12 rounded-xl"
                  >
                    <Upload className="w-4 h-4 mr-2" />
                    {logoPreview ? 'Cambiar' : 'Subir logo'}
                  </Button>
                  <p className="text-xs text-slate-500 mt-1">Opcional. PNG, JPG. Máx 5MB</p>
                </div>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/*"
                  onChange={handleLogoSelect}
                  className="hidden"
                  data-testid="register-logo-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="email" className="text-slate-700 font-medium">Email</Label>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="tu@email.com"
                  value={formData.email}
                  onChange={handleChange}
                  className="pl-12 h-14 text-base rounded-xl border-slate-300 focus:border-primary focus:ring-primary/20"
                  required
                  data-testid="register-email-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="phone" className="text-slate-700 font-medium">
                Teléfono <span className="text-slate-400 font-normal">(opcional)</span>
              </Label>
              <div className="relative">
                <Phone className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="phone"
                  name="phone"
                  type="tel"
                  placeholder="+34 600 000 000"
                  value={formData.phone}
                  onChange={handleChange}
                  className="pl-12 h-14 text-base rounded-xl border-slate-300 focus:border-primary focus:ring-primary/20"
                  data-testid="register-phone-input"
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label htmlFor="password" className="text-slate-700 font-medium">Contraseña</Label>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  name="password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Mínimo 6 caracteres"
                  value={formData.password}
                  onChange={handleChange}
                  className="pl-12 pr-12 h-14 text-base rounded-xl border-slate-300 focus:border-primary focus:ring-primary/20"
                  required
                  minLength={6}
                  data-testid="register-password-input"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 transition-colors"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            {/* Promo Code */}
            <div className="space-y-2">
              <Label htmlFor="promo_code" className="text-slate-700 font-medium flex items-center gap-2">
                <Ticket className="w-4 h-4 text-amber-500" />
                ¿Tienes un código promocional?
                <span className="text-slate-400 font-normal">(opcional)</span>
              </Label>
              <div className="relative">
                <Input
                  id="promo_code"
                  type="text"
                  placeholder="Ej: PROMO-ABC123"
                  value={promoCode}
                  onChange={handlePromoCodeChange}
                  className={`h-14 text-base rounded-xl font-mono uppercase tracking-wider ${
                    promoValid === true ? 'border-green-500 focus:border-green-500' : 
                    promoValid === false ? 'border-red-500 focus:border-red-500' : 
                    'border-slate-300 focus:border-primary'
                  }`}
                  data-testid="register-promo-input"
                />
                {validatingPromo && (
                  <div className="absolute right-4 top-1/2 -translate-y-1/2">
                    <div className="animate-spin w-5 h-5 border-2 border-primary border-t-transparent rounded-full"></div>
                  </div>
                )}
                {promoValid === true && !validatingPromo && (
                  <Check className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-green-500" />
                )}
              </div>
              {promoInfo && (
                <motion.div
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                  className={`p-3 rounded-lg text-sm ${
                    promoValid ? 'bg-green-50 text-green-700 border border-green-200' : 'bg-red-50 text-red-600 border border-red-200'
                  }`}
                >
                  {promoValid ? (
                    <div className="flex items-center gap-2">
                      <Gift className="w-5 h-5" />
                      <span>{promoInfo.message}</span>
                    </div>
                  ) : (
                    <span>{promoInfo.message}</span>
                  )}
                </motion.div>
              )}
            </div>

            {/* Terms checkbox */}
            <div className="flex items-start gap-3">
              <input
                type="checkbox"
                id="acceptTerms"
                checked={acceptedTerms}
                onChange={(e) => setAcceptedTerms(e.target.checked)}
                className="mt-1 w-4 h-4 rounded border-slate-300 text-primary focus:ring-primary/20"
                data-testid="accept-terms-checkbox"
              />
              <label htmlFor="acceptTerms" className="text-sm text-slate-600">
                He leído y acepto los{' '}
                <button
                  type="button"
                  onClick={() => setTermsModalOpen(true)}
                  className="text-primary font-medium hover:underline"
                >
                  Términos y Condiciones
                </button>
                {' '}y la{' '}
                <Link to="/privacidad" target="_blank" className="text-primary font-medium hover:underline">
                  Política de Privacidad
                </Link>
              </label>
            </div>

            <Button 
              type="submit" 
              className="w-full h-14 text-base font-semibold btn-primary"
              disabled={loading || !acceptedTerms}
              data-testid="register-submit-btn"
            >
              {loading ? 'Creando cuenta...' : (
                <>Crear cuenta gratis <ArrowRight className="w-5 h-5" /></>
              )}
            </Button>
          </form>

          <div className="mt-8 pt-8 border-t border-slate-200">
            <p className="text-center text-slate-600">
              ¿Ya tienes cuenta?{' '}
              <Link to="/login" className="text-primary font-semibold hover:underline">
                Inicia sesión
              </Link>
            </p>
          </div>
        </motion.div>
      </div>

      {/* Terms Modal */}
      <Dialog open={termsModalOpen} onOpenChange={setTermsModalOpen}>
        <DialogContent className="max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-xl">
              <FileText className="w-5 h-5 text-primary" />
              Términos y Condiciones
            </DialogTitle>
          </DialogHeader>
          
          <div className="prose prose-sm prose-slate max-w-none mt-4">
            <section className="mb-6">
              <h3 className="text-lg font-bold text-primary">1. Identificación del titular</h3>
              <p><strong>Responsable:</strong> Stampply | <strong>Denominación social:</strong> XP Projects SaaS | <strong>NIF/CIF:</strong> 46636052X</p>
            </section>

            <section className="mb-6">
              <h3 className="text-lg font-bold text-primary">2. Objeto</h3>
              <p>Stampply es una plataforma SaaS para comercios que permite crear y gestionar programas de fidelización mediante sellos digitales y la interacción del cliente final a través de WhatsApp y códigos QR.</p>
            </section>

            <section className="mb-6">
              <h3 className="text-lg font-bold text-primary">3. Aceptación</h3>
              <p>El uso de la plataforma implica la aceptación de estos Términos y Condiciones.</p>
            </section>

            <section className="mb-6">
              <h3 className="text-lg font-bold text-primary">4. Planes y precios</h3>
              <ul className="list-disc ml-4 space-y-1">
                <li><strong>Free (0€):</strong> 1 campaña, 1 local, 100 sellos/mes</li>
                <li><strong>Growth (29€/mes):</strong> 5 campañas, 1 local, 5.000 sellos/mes</li>
                <li><strong>Scale (95€/mes):</strong> 15 campañas, 5 locales, 25.000 sellos/mes</li>
                <li><strong>Elite (395€/mes):</strong> Ilimitado, hasta 50 locales</li>
              </ul>
            </section>

            <section className="mb-6">
              <h3 className="text-lg font-bold text-primary">5. Obligaciones del comercio</h3>
              <p>El comercio es responsable de cumplir la normativa aplicable, definir condiciones claras para sus programas y cumplir las recompensas ofrecidas.</p>
            </section>

            <section className="mb-6">
              <h3 className="text-lg font-bold text-primary">6. Propiedad intelectual</h3>
              <p>Stampply conserva todos los derechos sobre la plataforma. Se concede al comercio una licencia limitada para usar el servicio.</p>
            </section>

            <section className="mb-6">
              <h3 className="text-lg font-bold text-primary">7. Legislación aplicable</h3>
              <p>Se aplicará la legislación española. Las partes se someten a los juzgados de Barcelona, España.</p>
            </section>

            <div className="bg-slate-100 p-4 rounded-lg mt-6">
              <p className="text-sm text-slate-600 text-center">
                Para ver los términos completos, visita{' '}
                <Link to="/terminos" target="_blank" className="text-primary font-medium hover:underline">
                  esta página
                </Link>
              </p>
            </div>
          </div>

          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <Button variant="outline" onClick={() => setTermsModalOpen(false)}>
              Cerrar
            </Button>
            <Button 
              className="btn-primary"
              onClick={() => {
                setAcceptedTerms(true);
                setTermsModalOpen(false);
              }}
            >
              Aceptar términos
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default RegisterPage;
